
import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useDashboard } from '@/contexts/DashboardContext';
import { useEditorJS } from '@/hooks/useEditorJS';
import EditorWordStats from './parts/EditorWordStats';
import EditorPlaceholderButton from './parts/EditorPlaceholderButton';
import { Droppable } from 'react-beautiful-dnd';
import '@/styles/editor.css';
import { Button } from '@/components/ui/button';
import { Save } from 'lucide-react';

interface BlockEditorProps {
  initialData: any;
  onSave: (data: any) => Promise<boolean>;
  isSaving: boolean;
  isPublishing: boolean;
  articleId?: string; // Article ID for direct block updates
  onAddBlock?: () => void;
  droppableId: string;
}

export interface BlockEditorRef {
  insertBlock: () => void;
}

const BlockEditor = forwardRef<BlockEditorRef, BlockEditorProps>(({
  initialData,
  onSave,
  isSaving,
  isPublishing,
  articleId,
  onAddBlock,
  droppableId
}, ref) => {
  const editorInstanceRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const { refreshDashboardData } = useDashboard();
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);
  const [isSavingState, setIsSavingState] = useState(false);
  const saveInProgressRef = useRef(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const {
    initEditor,
    saveContent,
    insertBlock,
    destroyEditor,
    wordCount,
    readingTime,
    editorReady
  } = useEditorJS({
    initialData,
    holderRef: editorInstanceRef,
    articleId,
    onChange: (data) => {
      console.log("Editor content changed, changes will be saved automatically");
    }
  });

  useImperativeHandle(ref, () => ({
    insertBlock: () => {
      insertBlock();
    }
  }));

  useEffect(() => {
    addPlaceholderButton();
    initEditor();
    return () => {
      destroyEditor();
    };
  }, []);

  const addPlaceholderButton = () => {
    if (!editorInstanceRef.current) return;
    
    if (editorInstanceRef.current.querySelector('.block-placeholder-button')) {
      editorInstanceRef.current.querySelector('.block-placeholder-button')?.remove();
    }
    
    const placeholderButtonElement = document.createElement('div');
    placeholderButtonElement.appendChild(
      document.createRange().createContextualFragment(
        `<div class="block-placeholder-button">
          <button class="block-add-button"><span>+</span></button>
        </div>`
      )
    );
    
    const button = placeholderButtonElement.querySelector('button');
    if (button) {
      button.addEventListener('click', () => {
        insertBlock();
      });
    }
    
    editorInstanceRef.current.appendChild(placeholderButtonElement);
  };

  const handleSave = async () => {
    if (saveInProgressRef.current || isSaving) {
      toast({
        title: "Save in progress",
        description: "Please wait for the current save to complete",
      });
      return;
    }

    try {
      saveInProgressRef.current = true;
      setIsSavingState(true);
      
      console.log("Save button clicked, editor ready:", editorReady);
      if (!editorReady) {
        toast({
          title: "Editor not ready",
          description: "Please wait for the editor to initialize",
          variant: "destructive"
        });
        return;
      }
      
      const savedData = await saveContent();
      console.log("Editor data saved:", savedData);
      
      if (savedData) {
        toast({
          title: "Saving...",
          description: "Your article is being saved",
        });
        
        const success = await onSave(savedData);
        console.log("Save result:", success);
        
        if (success) {
          setLastSavedTime(new Date());
          toast({
            title: "Saved",
            description: "Your article has been saved as a draft",
          });
          refreshDashboardData();
        } else {
          toast({
            title: "Error",
            description: "Failed to save your article",
            variant: "destructive"
          });
        }
      } else {
        toast({
          title: "Error",
          description: "Could not retrieve editor content",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Save failed:', error);
      toast({
        title: "Error",
        description: "Failed to save your article: " + (error instanceof Error ? error.message : String(error)),
        variant: "destructive"
      });
    } finally {
      saveInProgressRef.current = false;
      setIsSavingState(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="block-editor space-y-6">
      <div className="editor-container max-w-[700px] mx-auto">
        <Droppable droppableId={droppableId}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`relative ${snapshot.isDraggingOver ? 'bg-accent/30 ring-2 ring-primary/40 rounded-lg' : ''}`}
              onDragEnter={() => setIsDraggingOver(true)}
              onDragLeave={() => setIsDraggingOver(false)}
              onDragEnd={() => setIsDraggingOver(false)}
            >
              {isDraggingOver && (
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-10">
                  <div className="text-lg font-medium text-muted-foreground bg-background/80 backdrop-blur-sm px-4 py-2 rounded-md shadow-sm">
                    Drop block here to add it to your article
                  </div>
                </div>
              )}
              <div 
                ref={editorInstanceRef} 
                className={`prose dark:prose-invert max-w-full min-h-[500px] bg-white dark:bg-gray-800 p-6 rounded-lg border border-border/60 shadow-sm transition-all ${snapshot.isDraggingOver ? 'opacity-50' : ''}`}
              />
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
      
      <EditorWordStats 
        wordCount={wordCount} 
        readingTime={readingTime} 
        lastSavedTime={lastSavedTime} 
      />
      
      <div className="flex justify-center mt-6">
        <Button 
          onClick={handleSave}
          disabled={isSaving || isSavingState}
          variant="outline"
          size="lg"
          className="px-8 py-6 h-auto font-medium text-base shadow-sm hover:shadow-md transition-all"
        >
          <Save className="mr-2 h-5 w-5" />
          {isSaving || isSavingState ? "Saving..." : "Save Article"}
        </Button>
      </div>
    </div>
  );
});

BlockEditor.displayName = 'BlockEditor';

export default BlockEditor;
